import React from 'react';
import { WebSocketState } from '../services/websocket-client';

export interface ConnectionStatusProps {
  readonly state: WebSocketState;
  readonly onReconnect: () => void;
}

/**
 * Connection status indicator.
 * Shows nothing when connected.
 * Shows reconnecting spinner, or disconnected with manual reconnect button.
 */
export function ConnectionStatus({ state, onReconnect }: ConnectionStatusProps): React.ReactElement | null {
  if (state === 'connected') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 bg-card border border-border rounded-lg shadow-lg px-4 py-3 flex items-center gap-3"
    >
      {state === 'connecting' && (
        <>
          <Spinner />
          <span className="text-sm text-muted-foreground">Connecting...</span>
        </>
      )}
      {state === 'reconnecting' && (
        <>
          <Spinner />
          <span className="text-sm text-amber-600 dark:text-amber-400">Reconnecting...</span>
        </>
      )}
      {state === 'disconnected' && (
        <>
          <span className="w-3 h-3 rounded-full bg-destructive" aria-hidden="true" />
          <span className="text-sm text-destructive">Disconnected</span>
          <button
            onClick={onReconnect}
            aria-label="Reconnect"
            className="text-sm text-primary hover:underline font-medium"
          >
            Reconnect
          </button>
        </>
      )}
      {state === 'session-expired' && (
        <>
          <span className="w-3 h-3 rounded-full bg-destructive" aria-hidden="true" />
          <span className="text-sm text-destructive">Session expired — please sign in again</span>
        </>
      )}
    </div>
  );
}

function Spinner(): React.ReactElement {
  return <span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />;
}
